import { useState, useEffect, useCallback } from 'react';

/**
 * Single source of truth for the account selected across the Portfolio pillar
 * (Overview / Performance / Orders). Backed by localStorage plus a window event
 * so a change on one tab live-syncs to the others — the tabs stay mounted under
 * TradingLayout (display:none), so a read-once default wouldn't propagate.
 *
 * The value is an account id (number) or null ("no concrete account", which the
 * Orders filter renders as "All accounts").
 */
const KEY = 'maghaweer.portfolio.selected_account_id';
const EVT = 'maghaweer:portfolio-account-change';

export const readSharedAccountId = () => {
  const s = localStorage.getItem(KEY);
  return s == null || s === '' ? null : Number(s);
};

export default function useSharedAccount() {
  const [id, setId] = useState(readSharedAccountId);

  const update = useCallback((next) => {
    const val = next == null || next === '' ? null : Number(next);
    setId(val);
    if (val == null) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, String(val));
    window.dispatchEvent(new CustomEvent(EVT, { detail: val }));
  }, []);

  useEffect(() => {
    const onEvt = (e) => setId(e.detail == null ? null : Number(e.detail));
    const onStorage = (e) => { if (e.key === KEY) setId(readSharedAccountId()); };
    window.addEventListener(EVT, onEvt);
    window.addEventListener('storage', onStorage); // other browser tabs
    return () => { window.removeEventListener(EVT, onEvt); window.removeEventListener('storage', onStorage); };
  }, []);

  return [id, update];
}
